import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { BAND, Btn, Eyebrow, H2, Muted, RevealSection } from '@/components/marketing/warm/primitives';
import { ROADMAP_ITEMS, ROADMAP_UPDATED, formatRoadmapDate, type RoadmapItem } from './roadmap-data';
import { setDocumentMeta, setCanonical, setOgImage, injectJsonLdList } from '@/lib/seo';

const STATUS_LABEL: Record<RoadmapItem['status'], string> = {
  shipped: 'Shipped',
  'in-progress': 'In development',
  planned: 'In the pipeline',
};

const STATUS_STYLE: Record<RoadmapItem['status'], React.CSSProperties> = {
  shipped: { background: 'var(--chip-green-bg)', color: 'var(--chip-green)' },
  'in-progress': { background: 'var(--chip-blue-bg)', color: 'var(--chip-blue)' },
  planned: { background: 'var(--warm-card)', color: 'var(--warm-muted)', border: '1px dashed var(--warm-border)' },
};

function StatusChip({ status }: { status: RoadmapItem['status'] }) {
  return (
    <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={STATUS_STYLE[status]}>
      {STATUS_LABEL[status]}
    </span>
  );
}

export function RoadmapPage() {
  useEffect(() => {
    setDocumentMeta(
      'Kora Roadmap — What\'s Coming Next',
      `Kora roadmap: what we are building next, in priority order: email-to-ticket, Slack, CSAT, MCP and more. Kora AI (reply drafting and resolution agent) has shipped. Updated ${formatRoadmapDate(ROADMAP_UPDATED)}.`,
    );
    setCanonical('/roadmap');
    setOgImage('/assets/og-roadmap.png');
    const cleanup = injectJsonLdList([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Kora Roadmap',
        url: 'https://usekora.app/roadmap',
        description: `Kora roadmap: planned features in priority order and the ship date of everything already shipped. Updated ${formatRoadmapDate(ROADMAP_UPDATED)}.`,
        dateModified: ROADMAP_UPDATED,
      },
    ]);
    return cleanup;
  }, []);

  const shipped = ROADMAP_ITEMS.filter((i) => i.status === 'shipped').sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  const inDevelopment = ROADMAP_ITEMS.filter((i) => i.status === 'in-progress').sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  const pipeline = ROADMAP_ITEMS.filter((i) => i.status === 'planned').sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));

  const Card = ({ item, dashed, n }: { item: RoadmapItem; dashed?: boolean; n?: number }) => (
    <div className="p-5 rounded-2xl h-full" style={dashed
      ? { border: '1.5px dashed var(--warm-border)', background: 'var(--warm-page)' }
      : { border: '1px solid var(--warm-border)', background: 'var(--warm-card)' }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'var(--warm-page)', color: 'var(--warm-muted)', border: '1px solid var(--warm-border)' }}>
          {item.category}
        </span>
        <StatusChip status={item.status} />
      </div>
      <p className="font-semibold text-sm mb-1.5" style={{ color: 'var(--warm-text)' }}>
        {n ? <span style={{ color: 'var(--warm-muted)' }}>{n}. </span> : null}{item.title}
      </p>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{item.description}</p>
      {item.status === 'shipped' ? (
        <p className="text-xs font-semibold mt-3" style={{ color: 'var(--warm-text)' }}>
          Shipped: {item.dateLabel ?? formatRoadmapDate(item.date)}
        </p>
      ) : null}
      {item.why ? <p className="text-xs leading-relaxed mt-1.5" style={{ color: 'var(--warm-muted)' }}>Why here: {item.why}</p> : null}
    </div>
  );

  return (
    <MarketingShell>
      <PageHero
        eyebrow="Roadmap"
        title={<>What's coming <br /><Muted><span className="italic">to Kora.</span></Muted></>}
        lead={`What we are building now, and what is in the pipeline after it. Priorities can change as we learn from customers. Last updated ${formatRoadmapDate(ROADMAP_UPDATED)}.`}
        actions={<>
          <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
          <Link to="/changelog" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>See the changelog →</Link>
        </>}
      />

      <RevealSection style={BAND}>
        <Eyebrow>In development</Eyebrow>
        <H2>Being built now</H2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          Our AI features are the focus right now. They share one knowledge base, so we are building them together.
        </p>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {inDevelopment.map((item) => <Card key={item.title} item={item} dashed />)}
        </div>
      </RevealSection>

      <RevealSection>
        <Eyebrow>In the pipeline</Eyebrow>
        <H2>Coming after, in order</H2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          How we ordered it: email intake is a must-have on helpdesk buying checklists and the most preferred way to contact support in a 2025 <a href="https://retailtimes.co.uk/yougov-british-consumers-prefer-human-centric-customer-service/" className="underline" rel="noopener">YouGov UK survey</a> (33% email, 31% phone); a <a href="https://www.capterra.com/resources/key-help-desk-software-features-for-smb-it-support/" className="underline" rel="noopener">Capterra review analysis</a> of small-business buyers puts ticket management, automation, a knowledge base, a self-service portal and reporting at the core. We then sequence by what unlocks the next feature. The order can change.
        </p>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pipeline.map((item, i) => <Card key={item.title} item={item} dashed n={i + 1} />)}
        </div>
      </RevealSection>

      <RevealSection>
        <Eyebrow>Live today</Eyebrow>
        <H2>Already shipped</H2>
        <div className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shipped.map((item) => <Card key={item.title} item={item} />)}
        </div>
      </RevealSection>

      <CtaBand />
    </MarketingShell>
  );
}
